import { create } from "zustand";
import {
  fetchWatchlistFilterOptions,
  fetchWatchlistGovStateOptions,
  fetchSnpRatingOptions,
  fetchMoodyRatingOptions,
  getBondTypeOptions,
  getSecurityChargeOptions,
  fetchWatchlistDefaultFilterValues,
} from "../http/api";
import { BOND_TYPES } from "../Constants/AppConstants";

// Shared cache for the SecMaster screen + search screen so the option lists and
// per-bond-type default ranges are fetched once and reused everywhere (no
// repeated calls when moving between the table and the search screen).

const YES_NO = [
  { value: "Y", label: "Yes" },
  { value: "N", label: "No" },
];

const toVL = (arr) =>
  (Array.isArray(arr) ? arr : [])
    .map((v) => {
      const s = `${v ?? ""}`.trim();
      return s ? { value: s, label: s } : null;
    })
    .filter(Boolean);

const toRatingVL = (arr) =>
  (Array.isArray(arr) ? arr : [])
    .filter((r) => r && `${r.rating ?? ""}`.trim())
    .map((r) => {
      const s = `${r.rating}`.trim();
      return { value: s, label: s };
    });

const toGovStateVL = (arr) =>
  (Array.isArray(arr) ? arr : [])
    .map((s) => {
      const code = `${s.stateCode ?? s.value ?? s.code ?? ""}`.trim();
      const name = `${s.stateName ?? s.name ?? code}`.trim();
      if (!code) return null;
      return { value: code, label: name && name !== code ? `${name} (${code})` : code };
    })
    .filter(Boolean);

const hasValue = (v) => v !== undefined && v !== null && v !== "";

// Bond type -> default-filter-values API "type" (uppercase, per the watchlist
// store's getWatchlistFilterApiType contract). Keyed by the SAME BOND_TYPES
// constants the search tabs use, so the lookup key can never drift from the
// bond type value passed in.
const BOND_TYPE_TO_DEFAULTS_API_TYPE = {
  [BOND_TYPES.TREASURY]: "GOV",
  [BOND_TYPES.CORPORATES]: "CORP",
  [BOND_TYPES.MUNICIPALS]: "MUNI",
};

// Primary (first) search tab. Its defaults are warmed eagerly on the SecMaster
// screen; Corporates & Municipals load lazily when their tab is first opened on
// the search screen. Matches the first entry of SEC_MASTER_SEARCH_TABS.
export const SEC_MASTER_PRIMARY_BOND_TYPE = BOND_TYPES.TREASURY;

// Mirrors the watchlist store's buildDefaultFilterDataFromResponse: only the
// coupon/maturity ranges are seeded from the defaults endpoint.
const buildDefaultFilterDataFromResponse = (response = {}) => {
  const defaults =
    response?.data && typeof response.data === "object" ? response.data : response || {};
  const next = {};
  if (hasValue(defaults.minCoupon)) next.couponMin = defaults.minCoupon;
  if (hasValue(defaults.maxCoupon)) next.couponMax = defaults.maxCoupon;
  if (hasValue(defaults.minMaturity)) next.maturityMin = defaults.minMaturity;
  if (hasValue(defaults.maxMaturity)) next.maturityMax = defaults.maxMaturity;
  return next;
};

const buildOptionsFromResults = ([filter, govState, snp, moody, bondTypes, secCharge]) => {
  let detectedError = null;
  for (const result of [filter, govState, snp, moody, bondTypes, secCharge]) {
    if (result.status === "rejected") {
      detectedError = "Something went wrong";
      break;
    }
    if (result.value != null && result.value?.success === false) {
      detectedError = result.value.message || "Something went wrong";
      break;
    }
  }

  const fd =
    filter.status === "fulfilled" ? filter.value?.data || filter.value || {} : {};

  const pick = (...keys) => {
    for (const k of keys) if (Array.isArray(fd[k])) return toVL(fd[k]);
    return [];
  };

  const options = {
    currency: pick("currencyList", "currency_list"),
    subType: pick("subTypeList", "sub_type_list"),
    sector: pick("sectorList", "sector_list"),
    issueCurrency: pick("issueCurrencyList", "issue_currency_list"),
    conventionMethod: pick("conventionMethodList", "convention_method_list"),
    interestPaymentFrequency: pick(
      "paymentFrequencyList",
      "payment_frequency_list",
      "interestPaymentFrequencyList"
    ),
    incomeSource: pick("incomeSourceList", "income_source_list"),
    couponType: pick("couponTypeList", "coupon_type_list"),
    issuer: pick("issuerList", "issuer_list"),
    securityCharge: secCharge.status === "fulfilled" ? toVL(secCharge.value) : [],
    bondType: bondTypes.status === "fulfilled" ? toVL(bondTypes.value).map(o => ({ value: o.value.toUpperCase(), label: o.label.toUpperCase() })) : [],
    govState: govState.status === "fulfilled" ? toGovStateVL(govState.value) : [],
    spRating: snp.status === "fulfilled" ? toRatingVL(snp.value) : [],
    moodyRating: moody.status === "fulfilled" ? toRatingVL(moody.value) : [],
    isPerpetual: YES_NO,
    isCallable: YES_NO,
    isZeroCoupon: YES_NO,
    isInsured: YES_NO,
    isExemptFromFedTax: YES_NO,
  };

  return { options, error: detectedError };
};

// Module-level in-flight guards so concurrent callers (SecMaster screen +
// search screen) share a single request rather than each firing their own.
let optionsInFlight = null;
// In-flight default-range requests, keyed by BOND TYPE (same key as
// `defaultsByType`), so concurrent callers share one request and we never fire a
// duplicate for a type that's already loading.
const defaultsRequestsByType = new Map();

export const useSecMasterOptionsStore = create((set, get) => ({
  options: {},
  optionsLoaded: false,
  optionsError: null,
  defaultsByType: {},

  loadOptions: (token, { force = false } = {}) => {
    if (!token) return undefined;
    if (optionsInFlight) return optionsInFlight;
    if (get().optionsLoaded && !force) return undefined;

    set({ optionsError: null });
    optionsInFlight = Promise.allSettled([
      fetchWatchlistFilterOptions(token),
      fetchWatchlistGovStateOptions(token),
      fetchSnpRatingOptions(token),
      fetchMoodyRatingOptions(token),
      getBondTypeOptions(token),
      getSecurityChargeOptions(token),
    ])
      .then((results) => {
        const { options, error } = buildOptionsFromResults(results);
        set({ options, optionsLoaded: true, optionsError: error });
      })
      .finally(() => {
        optionsInFlight = null;
      });

    return optionsInFlight;
  },

  refetchOptions: (token) => get().loadOptions(token, { force: true }),

  // Load the default coupon/maturity ranges for ONE bond type. The cache key is
  // the bond type itself (the single source of truth is `defaultsByType`):
  //   - already in defaultsByType        -> we have it, do nothing
  //   - a request is already in flight    -> reuse it, no duplicate call
  //   - otherwise                         -> fire one request, store under bondType
  // No "|| GOV" fallback: an unknown bond type simply isn't fetched, so it can
  // never silently reuse another type's result.
  loadDefaults: (token, bondType) => {
    if (!token || !bondType) return undefined;

    const apiType = BOND_TYPE_TO_DEFAULTS_API_TYPE[bondType];
    if (!apiType) return undefined;

    if (get().defaultsByType[bondType] !== undefined) return undefined;
    if (defaultsRequestsByType.has(bondType)) return defaultsRequestsByType.get(bondType);

    const request = fetchWatchlistDefaultFilterValues(token, { type: apiType })
      .then((response) => {
        const defaults = buildDefaultFilterDataFromResponse(response);
        set((s) => ({ defaultsByType: { ...s.defaultsByType, [bondType]: defaults } }));
        return defaults;
      })
      .catch(() => {
        // Leave defaultsByType[bondType] unset so a later attempt can retry.
        return undefined;
      })
      .finally(() => {
        defaultsRequestsByType.delete(bondType);
      });

    defaultsRequestsByType.set(bondType, request);
    return request;
  },

  // Warm the cache for the search screen. Deferred to a microtask so a screen's
  // primary data fetch (e.g. the SecMaster list, fired synchronously on mount)
  // grabs a connection first, then the options/defaults load in parallel.
  preload: (token) => {
    if (!token) return;
    Promise.resolve().then(() => {
      const { loadOptions, loadDefaults } = get();
      loadOptions(token);
      // Hybrid: warm only the first tab's defaults here; Corporates & Municipals
      // load lazily when their tab is first opened on the search screen.
      loadDefaults(token, SEC_MASTER_PRIMARY_BOND_TYPE);
    });
  },
}));
